import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../api/axiosInstance';

const BASE_URL = '/api/inventory';

// Types
export const fetchMiscellaneousTypes = createAsyncThunk(
  'miscellaneous/fetchTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/miscellaneous-types/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createMiscellaneousType = createAsyncThunk(
  'miscellaneous/createType',
  async (typeData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/miscellaneous-types/`, typeData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Miscellaneous Items
export const fetchMiscellaneous = createAsyncThunk(
  'miscellaneous/fetchMiscellaneous',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (filters.type) params.append('type', filters.type);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.student) params.append('student', filters.student);
      if (filters.class) params.append('class', filters.class);
      
      const response = await axiosInstance.get(`${BASE_URL}/miscellaneous/?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createMiscellaneous = createAsyncThunk(
  'miscellaneous/createMiscellaneous',
  async (miscData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/miscellaneous/`, miscData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateMiscellaneous = createAsyncThunk(
  'miscellaneous/updateMiscellaneous',
  async ({ id, ...miscData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/miscellaneous/${id}/`, miscData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteMiscellaneous = createAsyncThunk(
  'miscellaneous/deleteMiscellaneous',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/miscellaneous/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getMiscellaneousSummary = createAsyncThunk(
  'miscellaneous/getSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/miscellaneous/summary/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Payments
export const fetchMiscellaneousPayments = createAsyncThunk(
  'miscellaneous/fetchPayments',
  async (miscId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/miscellaneous-payments/?miscellaneous=${miscId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createMiscellaneousPayment = createAsyncThunk(
  'miscellaneous/createPayment',
  async (paymentData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/miscellaneous-payments/`, paymentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const miscellaneousSlice = createSlice({
  name: 'miscellaneous',
  initialState: {
    types: [],
    items: [],
    payments: [],
    summary: null,
    loading: false,
    saving: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Types
      .addCase(fetchMiscellaneousTypes.fulfilled, (state, action) => {
        state.types = action.payload;
      })
      .addCase(createMiscellaneousType.fulfilled, (state, action) => {
        state.types.push(action.payload);
      })
      // Miscellaneous Items
      .addCase(fetchMiscellaneous.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMiscellaneous.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchMiscellaneous.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createMiscellaneous.pending, (state) => {
        state.saving = true;
      })
      .addCase(createMiscellaneous.fulfilled, (state, action) => {
        state.saving = false;
        state.items.unshift(action.payload);
      })
      .addCase(createMiscellaneous.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updateMiscellaneous.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) state.items[index] = action.payload;
      })
      .addCase(deleteMiscellaneous.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item.id !== action.payload);
      })
      .addCase(getMiscellaneousSummary.fulfilled, (state, action) => {
        state.summary = action.payload;
      })
      // Payments
      .addCase(fetchMiscellaneousPayments.fulfilled, (state, action) => {
        state.payments = action.payload;
      })
      .addCase(createMiscellaneousPayment.fulfilled, (state, action) => {
        state.payments.push(action.payload);
        // Update item payment status
        const item = state.items.find(item => item.id === action.payload.miscellaneous);
        if (item) {
          item.payments = item.payments || [];
          item.payments.push(action.payload);
        }
      });
  },
});

export const { clearError } = miscellaneousSlice.actions;
export default miscellaneousSlice.reducer;
